import React, { useEffect, useState } from "react";
import { LogOut, Home, MessageSquare, Menu, X, Image as ImageIcon, Sparkles } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { createPost, fetchPosts } from "../../../features/postSlice";
import { fetchContacts } from "../../../features/chatSlice";
import { formatPostTime } from "../../../utils/formatTime";
import bannerimg from "../../../assets/img/banner.jpeg";
import { Link, useNavigate } from "react-router-dom";
import avater from "../../../assets/img/avater.png";
import Post from "../profile/Post.jsx";
import { logout, profileInfo } from "../../../features/userSlice.js";
import API from "../../../utils/Api";
import io from "socket.io-client";

const socket = io("http://localhost:5000");

const TimelinePage = () => {
  const [postContent, setPostContent] = useState("");
  const [postImage, setPostImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const dispatch = useDispatch();
  const { posts } = useSelector((state) => state.post);
  const { user } = useSelector((state) => state.user);
  const { contacts = [] } = useSelector((state) => state.chat);
  const navigate = useNavigate();
  const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
  const resolveAvatar = (url) =>
    url ? (url.startsWith("http") ? url : `${apiBase}${url}`) : "";
  const [toast, setToast] = useState("");
  const unreadTotal = contacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  useEffect(() => {
    dispatch(fetchPosts());
    dispatch(profileInfo());
    dispatch(fetchContacts());
  }, [dispatch]);

  useEffect(() => {
    if (!user?._id) return;
    socket.emit("joinChat", user._id);
    const handleReceiveMessage = async (msg) => {
      if (msg.sender === user._id) return;
      try {
        const res = await API.get(`/users/${msg.sender}`);
        const senderName = res.data?.data?.name || res.data?.data?.username || "Someone";
        setToast(`${senderName} messaged you`);
        setTimeout(() => setToast(""), 2500);
        dispatch(fetchContacts());
      } catch {
        setToast("New message received");
        setTimeout(() => setToast(""), 2500);
        dispatch(fetchContacts());
      }
    };

    socket.on("receiveMessage", handleReceiveMessage);
    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
    };
  }, [user?._id, dispatch]);

  const savePost = () => {
    if (!postContent.trim() && !postImage) return;

    dispatch(
      createPost({
        username: user?.name || user?.username || "Unknown",
        content: postContent.trim(),
        imageFile: postImage,
      })
    );

    setPostContent("");
    setPostImage(null);
    setImagePreview("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setPostImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  /* === Sidebar Content (Reusable) === */
  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white rounded-md overflow-hidden border border-gray-200">
  {/* ================= PROFILE CARD ================= */}
  <div className="bg-indigo-600 p-4 flex flex-col items-center text-white">
    <img
      src={resolveAvatar(user?.avatarUrl) || avater}
      alt="Profile"
      className="w-20 h-20 rounded-full border-2 border-white mb-3 object-cover"
    />
    <h2 className="font-bold text-lg truncate">{user?.name || "My Profile"}</h2>
    <p className="text-sm text-indigo-100 truncate">{user?.username || "Username"}</p>
    <p className="text-xs mt-1 text-indigo-200 text-center">Welcome back!</p>
  </div>

  {/* ================= MENU (BOTTOM) ================= */}
  <div className="flex-1 flex flex-col justify-end p-4">
    <ul className="space-y-2">
      {/* Timeline */}
      <li className="flex items-center p-2 text-indigo-600 bg-indigo-50 rounded-md font-medium hover:bg-indigo-100 transition cursor-pointer">
        <Home size={20} className="mr-3" /> Timeline
      </li>

      {/* Messenger */}
      <li>
        <Link
          to="/chat"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition"
        >
          <MessageSquare size={20} className="mr-3 text-indigo-600" />
          <span className="flex-1">Messenger</span>
          {unreadTotal > 0 && (
            <span className="text-xs bg-indigo-600 text-white rounded-full px-2 py-0.5">
              {unreadTotal}
            </span>
          )}
        </Link>
      </li>

      {/* Divider */}
      <div className="border-t border-gray-200 my-3"></div>

      {/* Profile */}
      <li>
        <Link
          to="/profile"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition"
        >
          <img
            src={resolveAvatar(user?.avatarUrl) || avater}
            alt="Profile"
            className="w-6 h-6 rounded-full mr-3 border"
          />
          <span className="text-sm font-medium truncate">
            {user?.name
              ? user.name.length > 12
                ? user.name.slice(0, 12) + "..."
                : user.name
              : "My Profile"}
          </span>
        </Link>
      </li>

      {/* Logout */}
      <li
        onClick={() => {
          dispatch(logout());
          navigate("/login");
        }}
        className="flex items-center p-2 text-gray-700 hover:bg-red-50 rounded-md cursor-pointer transition"
      >
        <LogOut size={20} className="mr-3 text-red-500" /> Logout
      </li>
    </ul>
  </div>
</div>

  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* === Mobile Header === */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-4 py-3">
        <button onClick={() => setSidebarOpen(true)}>
          <Menu size={26} />
        </button>
        <h2 className="text-xl font-semibold text-indigo-600 text-left flex items-center gap-2">
          <Sparkles size={18} /> সংযোগ
        </h2>
        <div className="w-6" />
      </header>

      {/* === Mobile Sidebar Overlay === */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* === Mobile Sidebar === */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 transform transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:hidden`}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="font-semibold text-lg">Menu</h3>
          <button onClick={() => setSidebarOpen(false)}>
            <X />
          </button>
        </div>
        <SidebarContent />
      </aside>

      {/* === Main Layout === */}
      <div className="max-w-6xl mx-auto pt-6 md:pt-6 pb-12 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-6 px-4">
        {/* === Desktop Sidebar === */}
        <nav className="hidden md:block md:col-span-1 lg:col-span-3">
          <div className="sticky top-20 bg-white rounded-md border border-gray-200">
            <SidebarContent />
          </div>
        </nav>

        {/* === Center Feed === */}
        <div className="md:col-span-3 lg:col-span-6">
          <div className="bg-white p-5 rounded-md border border-gray-200 mb-6 relative">
            {/* Title */}
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-indigo-600 rounded-full"></span>
              সংযোগে আপনার মতামত শেয়ার করুন
            </h2>

            <textarea
              placeholder="What's on your mind?"
              rows="3"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              className="w-full p-3 pr-20 border rounded-md focus:ring-2 focus:ring-indigo-500 resize-none text-sm"
            />

            <div className="flex items-center justify-between mt-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-indigo-600">
                <ImageIcon size={18} />
                Add Photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>

              {postImage && (
                <button
                  type="button"
                  onClick={() => {
                    if (imagePreview) {
                      URL.revokeObjectURL(imagePreview);
                    }
                    setPostImage(null);
                    setImagePreview("");
                  }}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  Remove photo
                </button>
              )}
            </div>

            {imagePreview && (
              <div className="mt-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full max-h-80 object-cover rounded-md border"
                />
              </div>
            )}

            {/* Post Button inside textarea */}
            <button
              onClick={savePost}
              className="absolute bottom-8 right-7 px-5 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition"
            >
              Post
            </button>
          </div>

          {posts.map((post) => { 
            const { timeAgo, fullDate } = formatPostTime(post.createdAt);
            return (
              <Post
                key={post._id}
                postId={post._id}
                username={post.author.name || post.author.username}
                userId={post.author._id}
                handle={fullDate}
                avatar={post.author.avatarUrl}
                time={timeAgo}
                content={post.content}
                imageUrl={post.imageUrl}
                likes={post.likes}
                comments={post.comments}
              />
            );
          })}
        </div>

       {/* === Right Sidebar === */}
<aside className="hidden lg:block lg:col-span-3">
  <div className="sticky top-20 space-y-6">
    {/* Banner Card */}
    <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
      <img
        src={bannerimg}
        alt="Banner"
        className="w-full h-48 object-cover"
      />
      <div className="p-4 text-center">
        <h3 className="text-lg font-semibold text-gray-800">Stay Connected</h3>
        <p className="text-sm text-gray-500 mt-1">
          Follow the latest updates and policies
        </p>
      </div>
    </div>

    {/* Footer Links */}
    <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
      <Link to="/privacy-policy" className="hover:text-indigo-600 transition">Privacy Policy</Link>
      <span className="text-gray-300">|</span>
      <Link to="/terms" className="hover:text-indigo-600 transition">Terms of Service</Link>
      <span className="text-gray-300">|</span>
      <Link to="/contact" className="hover:text-indigo-600 transition">Contact</Link>
      <span className="text-gray-300">|</span>
      <Link to="/help" className="hover:text-indigo-600 transition">Help Center</Link>
    </div>

    {/* Copyright */}
    <p className="text-center text-gray-400 text-xs mt-2">
      &copy; {new Date().getFullYear()} Songzug. All rights reserved.
    </p>
  </div>
</aside>

      </div>
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2 rounded shadow">
          {toast}
        </div>
      )}
    </div>
  );
};

export default TimelinePage;
